/**
 * The "Total (X)" hedge box — a CALCULATOR, and nothing else.
 *
 * `agent-console.yaml` non_goals[1] ("NOT a rules/pricing engine … it never
 * configures trading logic") and non_goals[2] ("NOT a player/betting app — no
 * bet placement") make that non-negotiable, so the strip carries a permanent
 * line saying so rather than relying on the absence of a Place button to
 * communicate it. Take the figure to your exchange terminal.
 *
 * The maths lives in `@/lib/book` where `book.test.ts` pins it against the
 * recording's own numbers; this file is the form, the guards and the wording.
 */

import { useMemo, useState, type FormEvent } from 'react'
import { Button, Chip, Field, Input, MoneyInput, Select } from '@w7/ui'
import { flattenTarget, hedgeToTarget, round2, tick, tickUp, type HedgeResult } from '@/lib/book'
import { money, moneyCompact, odds } from '@/lib/format'
import type { BookPosition, RunnerPrices } from '@/data/types'

/**
 * A pressed Update, frozen.
 *
 * `positions` is the book the figure was computed against, kept so the page can
 * detect that a 30-second poll has moved the book underneath a hedge the
 * operator is still reading. `simulatedPrice` is the whole of invariant I1 in
 * one boolean: while it is true the output inherits the ladder's simulated
 * marker, and the moment the operator types a real price from their terminal it
 * drops off.
 */
export interface HedgeCommit {
  selection: string
  price: number
  target: number
  simulatedPrice: boolean
  positions: BookPosition[]
  result: HedgeResult
  computedAt: number
}

/** Value equality on the book — the "has it moved since you calculated" test. */
export function samePositions(a: BookPosition[], b: BookPosition[]): boolean {
  if (a.length !== b.length) return false
  return a.every((entry, i) => entry.name === b[i]?.name && entry.net === b[i]?.net)
}

const worstOf = (positions: BookPosition[]): BookPosition | null =>
  positions.reduce<BookPosition | null>(
    (worst, entry) => (worst === null || entry.net < worst.net ? entry : worst),
    null,
  )

/** Best back price and the size showing at it — `back` is outermost-first. */
function bestBackOf(prices: RunnerPrices[], selection: string) {
  const runner = prices.find((entry) => entry.selection === selection)
  const level = runner?.back[runner.back.length - 1]
  return level ? { price: level.price, size: level.size } : null
}

export function HedgeBox({
  positions,
  prices,
  matched,
  basis,
  commit,
  moved,
  onCommit,
  onSwitchBasis,
}: {
  positions: BookPosition[]
  prices: RunnerPrices[]
  /** The whole matched book on this market — the sanity bound on a stake. */
  matched: number
  basis: 'my' | 'total'
  commit: HedgeCommit | null
  /** True when the live book no longer equals the one `commit` was built on. */
  moved: boolean
  onCommit: (next: HedgeCommit | null) => void
  onSwitchBasis: () => void
}) {
  const worst = useMemo(() => worstOf(positions), [positions])

  const [picked, setPicked] = useState<string | null>(null)
  const selection = picked && positions.some((p) => p.name === picked) ? picked : (worst?.name ?? '')

  const ladder = bestBackOf(prices, selection)
  const [priceText, setPriceText] = useState('')
  const [priceEdited, setPriceEdited] = useState(false)
  // Untouched, the field mirrors the ladder so it re-defaults when the runner
  // changes. Once typed, the operator's number wins and never gets overwritten
  // by a poll.
  const priceValue = priceEdited ? priceText : ladder ? odds(ladder.price) : ''

  const [targetText, setTargetText] = useState('')
  const [showErrors, setShowErrors] = useState(false)

  const parsedPrice = Number(priceValue)
  const priceError =
    priceValue.trim() === '' || !Number.isFinite(parsedPrice) || parsedPrice <= 1.01
      ? 'Decimal odds must be above 1.01.'
      : null
  const parsedTarget = Number(targetText)
  const targetError =
    targetText.trim() === '' || !Number.isFinite(parsedTarget)
      ? 'Enter a target, or press Flatten.'
      : null

  const disabled = basis === 'my'

  const pickSelection = (next: string) => {
    setPicked(next)
    // A price typed for one runner is not a price for another — carrying it
    // across would be a mixed input, which is the class of error this whole
    // screen exists to remove.
    setPriceEdited(false)
    setPriceText('')
  }

  const flatten = () => {
    if (priceError) {
      setShowErrors(true)
      return
    }
    const level = flattenTarget(positions, selection, parsedPrice)
    if (level !== null) setTargetText(String(level))
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setShowErrors(true)
    if (priceError || targetError) return
    const result = hedgeToTarget(positions, selection, parsedPrice, parsedTarget)
    if (!result) return
    onCommit({
      selection,
      price: parsedPrice,
      target: parsedTarget,
      simulatedPrice: !priceEdited,
      positions,
      result,
      computedAt: Date.now(),
    })
  }

  /* ------------------------------------------------------------ warnings -- */

  const warnings: string[] = []
  if (commit) {
    const { result } = commit
    if (result.stake === 0) {
      warnings.push('Your worst case is already above that target — there is nothing to place.')
    }
    if (result.overshoots) {
      const bottom = worstOf(result.positions)
      warnings.push(
        `This lifts ${commit.selection} to ${money(commit.target)} but takes ${
          bottom?.name ?? 'another runner'
        } below it. The worst case after the bet is ${money(result.worstCase)}.`,
      )
    }
    const committedLadder = bestBackOf(prices, commit.selection)
    if (commit.simulatedPrice && committedLadder && result.stake > committedLadder.size) {
      warnings.push(
        `Top of book shows ${moneyCompact(
          committedLadder.size,
        )} at that price (simulated). A stake this size would move the price.`,
      )
    }
    if (result.stake > matched) {
      warnings.push('That is larger than the whole matched book on this market.')
    }
  }

  /* --------------------------------------------------------------- basis -- */

  // Nothing to hedge against yet: the market has not loaded, or the exchange
  // returned no runners. A Select with no options and an empty value is a
  // React warning and an unusable control, not a degraded one.
  if (positions.length === 0) {
    return (
      <div className="w7-hedge">
        <p className="w7-hedge-note">
          Calculator only. Nothing here places a bet or changes a limit — take the figure to your
          exchange terminal.
        </p>
      </div>
    )
  }

  // A hedge lands on the WHOLE book, not on your share of it. Total-book maths
  // inside a panel toggled to "my share" would be a mixed-basis figure — the
  // exact class of error this console exists to stop — so the calculator is
  // disabled rather than silently converted.
  if (disabled) {
    return (
      <div className="w7-hedge">
        <p className="w7-hedge-note">
          Calculator only. Nothing here places a bet or changes a limit — take the figure to your
          exchange terminal.
        </p>
        <div className="w7-mb-hedge-locked">
          <span>
            A hedge lands on the whole book, not on your share of it. Switch to Total book to
            calculate one.
          </span>
          <Button variant="secondary" size="sm" onClick={onSwitchBasis}>
            Switch to total book
          </Button>
        </div>
      </div>
    )
  }

  // The step below is derived from `tickUp`, never from a second copy of the
  // band table — two copies of that table is exactly how a ladder starts
  // quoting prices the exchange does not trade.
  const step =
    Number.isFinite(parsedPrice) && parsedPrice > 1.01
      ? Math.max(0.01, round2(tickUp(parsedPrice) - tick(parsedPrice)))
      : 0.01

  return (
    // `noValidate`: the guards below are visible sentences with our own wording,
    // and the browser's own bubble would block Enter-to-submit before any of
    // them ran.
    <form className="w7-hedge" onSubmit={submit} noValidate>
      <p className="w7-hedge-note">
        Calculator only. Nothing here places a bet or changes a limit — take the figure to your
        exchange terminal.
      </p>

      <Field label="Bet on" className="w7-mb-hedge-field">
        {({ id }) => (
          <Select
            id={id}
            value={selection}
            onChange={(event) => pickSelection(event.target.value)}
            options={positions.map((entry) => ({ value: entry.name, label: entry.name }))}
          />
        )}
      </Field>

      <Field
        label="At price"
        className="w7-mb-hedge-field"
        hint="Defaults to the simulated ladder. Type the real price from your terminal for a figure you can act on."
        error={showErrors ? priceError : null}
      >
        {({ id, describedBy, invalid }) => (
          <Input
            id={id}
            numeric
            aria-describedby={describedBy}
            invalid={invalid}
            value={priceValue}
            step={step}
            type="number"
            onChange={(event) => {
              setPriceEdited(true)
              setPriceText(event.target.value)
            }}
          />
        )}
      </Field>

      <Field
        label="Target total (X)"
        className="w7-mb-hedge-field"
        hint="Negatives are allowed — a capped loss is a legitimate target."
        error={showErrors ? targetError : null}
      >
        {({ id, describedBy, invalid }) => (
          <MoneyInput
            id={id}
            aria-describedby={describedBy}
            invalid={invalid}
            value={targetText}
            type="number"
            onChange={(event) => setTargetText(event.target.value)}
          />
        )}
      </Field>

      <div className="w7-mb-hedge-actions">
        <Button variant="ghost" onClick={flatten}>
          Flatten
        </Button>
        <Button variant={commit && !moved ? 'secondary' : 'primary'} type="submit">
          Update
        </Button>
      </div>

      <p className="w7-mb-hedge-out" aria-live="polite">
        {commit ? (
          <>
            <span>
              Back {commit.selection} for {money(commit.result.stake)} at {odds(commit.price)}. Worst
              case becomes {money(commit.result.worstCase)}.
            </span>
            {commit.simulatedPrice ? (
              <Chip size="sm" tone="warn" icon="warning">
                Simulated price
              </Chip>
            ) : null}
            {moved ? (
              <Chip size="sm" tone="warn" icon="warning">
                Recalculate — the book has moved
              </Chip>
            ) : null}
          </>
        ) : (
          <span className="w7-dim">
            Enter a target and press Update, or press Flatten to level the book.
          </span>
        )}
      </p>

      {warnings.map((warning) => (
        <p className="w7-mb-warn" key={warning}>
          {warning}
        </p>
      ))}
    </form>
  )
}
